import { computed, inject, Injectable, signal, Type } from '@angular/core';
import { SchedulerFormService } from './scheduler-form.service';
import { EnrollmentSchedulingService } from '@lb/api/mylb/services/enrollment-scheduling.service';
import { IEnrollment, IEnrollmentNote, IEnrollmentScheduleRequest, IEnrollmentStatus } from '@lb/api/mylb/interfaces';
import { ScheduleConfigurationComponent } from '../components/schedule-configuration/schedule-configuration.component';
import { SchedulePreferredCalendarComponent } from '../components/schedule-preferred-calendar/schedule-preferred-calendar.component';
import { ScheduleCalendarComponent } from '../components/schedule-calendar/schedule-calendar.component';
import { ScheduleReviewComponent } from '../components/schedule-review/schedule-review.component';
import { EnrollmentStatus } from '@lb/api/mylb/enums/enrollment-status.enum';
import { Observable, of, take } from 'rxjs';
import { EnrollmentService } from '@lb/api/mylb/services/enrollment.service';
import { ProgramService } from '@lb/api/mylb/services/program.service';
import { IProgramFrequency } from '@lb/api/mylb/interfaces/iprogram-frequency.interface';
import { MDStepperStep } from '@melodic-ui/components';

export type SchedulerStep = MDStepperStep & {
	stepComponent: Type<unknown>;
	stepComponentTitle?: string;
};

@Injectable({
	providedIn: 'root'
})
export class SchedulerStateService {
	private readonly _schedulerFormService = inject(SchedulerFormService);
	private readonly _enrollmentSchedulingService = inject(EnrollmentSchedulingService);
	private readonly _enrollmentService = inject(EnrollmentService);
	private readonly _programService = inject(ProgramService);

	// Private writable signals
	private readonly _enrollment = signal<IEnrollment | null>(null);
	private readonly _isAdmin = signal<boolean>(false);
	private readonly _currentStepIndex = signal<number>(0);
	private readonly _enrollmentStatuses = signal<IEnrollmentStatus[]>([]);
	private readonly _programFrequencies = signal<IProgramFrequency[]>([]);

	// Public readonly signals
	enrollment = this._enrollment.asReadonly();
	isAdmin = this._isAdmin.asReadonly();
	currentStepIndex = this._currentStepIndex.asReadonly();
	enrollmentStatuses = this._enrollmentStatuses.asReadonly();
	programFrequencies = this._programFrequencies.asReadonly();

	enrollmentStatus = computed(() => this._enrollment()?.enrollmentStatusID ?? null);
	initialTimezone = computed(() => this._enrollment()?.learnerTimeZone ?? null);
	enrollmentNotes = computed(() => this.enrollment()?.notes ?? []);

	canChangePreferredTimes = computed<boolean>(() => {
		const status = this.enrollmentStatus();
		const canChange = status === EnrollmentStatus.AwaitingMatch || status === EnrollmentStatus.AwaitingSchedule;
		return !this.isAdmin() || (canChange && this.isAdmin());
	});
	canChangeTimezone = computed<boolean>(() => this.initialTimezone() === null);

	schedulerSteps = computed(() => {
		return [
			{
				id: 1,
				title: 'Enter Schedule Details',
				description: 'Enter the start date, lesson frequency and set the learner’s time zone.',
				stepComponent: ScheduleConfigurationComponent
			},
			{
				id: 2,
				title: 'Select Preferred Lesson Time',
				description: 'Select the preferred lesson day(s) and time(s). Lessons are 1 hour long.',
				stepComponent: SchedulePreferredCalendarComponent,
				stepComponentTitle: 'Select Preferred Lesson Time'
			},
			{
				id: 3,
				title: 'Share Additional Availability',
				description: 'Share additional available time each week that may be used for instructor matching.',
				stepComponent: ScheduleCalendarComponent,
				stepComponentTitle: 'Share Additional Availability'
			},
			{
				id: 4,
				title: 'Submit Requested Schedule',
				description: 'Check the proposed schedule including the expected end date.',
				stepComponent: ScheduleReviewComponent,
				stepComponentTitle: 'Submit Requested Schedule'
			}
		];
	});

	currentSchedulerStep = computed(() => this.schedulerSteps()[this.currentStepIndex()]);
	isFirstStep = computed(() => this.currentStepIndex() === 0);
	isLastStep = computed(() => this.currentStepIndex() === this.schedulerSteps().length - 1);
	canProceedToNextStep = computed<boolean>(() => this._schedulerFormService.getStepValidationSignal(this.currentStepIndex())());
	canFinish = computed(() => this.isLastStep() && this._schedulerFormService.canProceedFromStep(0) && this._schedulerFormService.canProceedFromStep(1));

	completedSchedulerSteps = computed<number[]>(() => {
		const completedSteps: number[] = [];

		this.schedulerSteps().forEach((step, index) => {
			if (this._schedulerFormService.getStepValidationSignal(index)()) {
				completedSteps.push(step.id);
			}
		});

		return completedSteps;
	});
	isAllStepsComplete = computed<boolean>(() => this.completedSchedulerSteps().length === this.schedulerSteps().length);

	// Computed signal for dynamic tooltip message explaining why Next button is disabled
	// The errors object comes from Angular form validators defined in SchedulerV2FormService
	// Each validator returns ValidationErrors with this structure:
	// {
	//   'errorKey': { message: 'User-friendly message', ...additionalData }
	// }
	// Available errors by step:
	//   Step 0: frequencyRequired, startDateRequired, startDateTooEarly
	//   Step 1: invalidScheduleCount, missingFrequency
	//   Step 2: invalidScheduleCount, invalidTimeBlock, missingFrequency
	nextButtonTooltip = computed<string>(() => {
		// Only show tooltip when button is disabled
		if (this.canProceedToNextStep()) {
			return '';
		}

		const stepIndex = this.currentStepIndex();

		// Access the errors signal to create reactive dependency
		// This ensures the computed signal re-evaluates when form validation errors change
		const errors = this._schedulerFormService.getStepErrorsSignal(stepIndex)() || {};

		// Step 0 (Configuration) validation messages
		if (stepIndex === 0) {
			// Check for specific errors and return their messages
			if (errors['frequencyRequired']) {
				return errors['frequencyRequired'].message;
			}
			if (errors['startDateRequired']) {
				return errors['startDateRequired'].message;
			}
			if (errors['startDateTooEarly']) {
				return errors['startDateTooEarly'].message;
			}
			// Generic message if we have errors but no specific message
			return 'Please complete all required fields';
		}

		// Step 1 (Preferred Times) validation messages
		if (stepIndex === 1) {
			if (errors['invalidScheduleCount']) {
				// This error has a dynamic message with the required frequency
				return errors['invalidScheduleCount'].message;
			}
			if (errors['missingFrequency']) {
				return 'Configuration error: Please go back and set a lesson frequency';
			}
			return 'Select additional preferred time to continue';
		}

		// Step 2 (Additional Availability) validation messages
		if (stepIndex === 2) {
			if (errors['invalidScheduleCount']) {
				// This error has a dynamic message with required hours
				return errors['invalidScheduleCount'].message;
			}
			if (errors['invalidTimeBlock']) {
				return errors['invalidTimeBlock'].message;
			}
			if (errors['missingFrequency']) {
				return 'Configuration error: Please go back and set a lesson frequency';
			}
			return 'You need to select a minimum number of hour(s) to proceed.';
		}

		// Step 3 (Review) - should not have validation errors
		if (stepIndex === 3) {
			return 'Please review your schedule';
		}

		// Default message
		return 'Please complete the current step';
	});

	// Computed signal for Finish button tooltip
	finishButtonTooltip = computed<string>(() => {
		// Only show tooltip when button is disabled
		if (this.canFinish()) {
			return '';
		}

		return 'Please complete all required steps';
	});

	public goToNextStep(): void {
		if (this.currentStepIndex() < this.schedulerSteps().length - 1) {
			let nextIndex = this.currentStepIndex() + 1;

			if (!this.canChangePreferredTimes() && nextIndex === 1) {
				nextIndex = 2;
			}

			this._currentStepIndex.set(nextIndex);
		}
	}

	public goToPreviousStep(): void {
		if (this.currentStepIndex() > 0) {
			let nextIndex = this.currentStepIndex() - 1;

			if (!this.canChangePreferredTimes() && nextIndex === 1) {
				nextIndex = 0;
			}

			this._currentStepIndex.set(nextIndex);
		}
	}

	public setCurrentSchedulerStepIndex(index: number): void {
		if (index >= 0 && index < this.schedulerSteps().length) {
			this._currentStepIndex.set(index);
		}
	}

	public initializeState(isAdmin: boolean, enrollment: IEnrollment | null): void {
		this.resetSchedulerState();
		this._isAdmin.set(isAdmin);
		this._enrollment.set(enrollment);
		this.loadEnrollmentStatus();
		this.loadProgramFrequencies();
	}

	public resetSchedulerState(): void {
		this._currentStepIndex.set(0);
		this._isAdmin.set(false);
		this._enrollment.set(null);
	}

	public loadProgramFrequencies(): void {
		const enrollment = this._enrollment();
		const programID = enrollment?.program?.programID;

		// If no program ID (unlikely for enrolled students), might fallback to no filter or skip
		// But let's filter if we have it
		this._programService
			.getProgramFrequencies(programID)
			.pipe(take(1))
			.subscribe((frequencies) => {
				this._programFrequencies.set(frequencies);
			});
	}

	public completeSchedulerStep(enrollmentID: number, enrollmentNotes: IEnrollmentNote[]): Observable<boolean> {
		if (!this.isLastStep() || !this.canFinish()) {
			return of(false);
		}

		const languageExperience = enrollmentNotes.find((x) => x.noteTypeID === 1)?.note || '';
		const learningNeeds = enrollmentNotes.find((x) => x.noteTypeID === 2)?.note || '';
		const requestedInstructor = enrollmentNotes.find((x) => x.noteTypeID === 4)?.note || '';

		const formData = this._schedulerFormService.getFormData();

		// Helper function to subtract 1 minute from end time to avoid midnight issues
		const adjustEndTime = (endTime: string): string => {
			if (endTime === '00:00') {
				return '23:59';
			}

			const [hours, minutes] = endTime.split(':').map(Number);
			const totalMinutes = hours * 60 + minutes - 1;
			const adjustedHours = Math.floor(totalMinutes / 60);
			const adjustedMinutes = totalMinutes % 60;
			return `${String(adjustedHours).padStart(2, '0')}:${String(adjustedMinutes).padStart(2, '0')}`;
		};

		const enrollmentSchedulingRequest: IEnrollmentScheduleRequest = {
			startDate: formData.configuration?.startDate || '',
			lessonFrequency: formData.configuration?.frequency || 0,
			timezoneID: formData.configuration?.timeZone || 0,
			enrollmentSchedule: (this._schedulerFormService.buildScheduleSavePayload(2) || []).map((s) => ({
				dayIndex: s.dayIndex,
				startTime: s.startTime,
				endTime: adjustEndTime(s.endTime),
				enrollmentID: 0, // enrollmentID supplied via route param in API path
				createDate: new Date(),
				timezoneID: s.timezoneID
			})),
			// Use canonical build method so timezoneID is preserved on each merged schedule
			enrollmentAvailabilities: (this._schedulerFormService.buildScheduleSavePayload(3) || []).map((s) => ({
				dayIndex: s.dayIndex,
				startTime: s.startTime,
				endTime: adjustEndTime(s.endTime),
				enrollmentID: 0, // enrollmentID supplied via route param in API path
				createDate: new Date(),
				timezoneID: s.timezoneID
			})),
			languageExperience: this.buildNoteString(languageExperience, formData.reviewData?.languageExperience),
			learningNeeds: this.buildNoteString(learningNeeds, formData.reviewData?.learningNeeds),
			requestedInstructor: this.buildNoteString(requestedInstructor, formData.reviewData?.requestedInstructor),
			currentEnrollmentStatus: this.isAdmin() ? this.enrollmentStatus() : undefined
		};

		return this._enrollmentSchedulingService.saveEnrollmentSchedule(enrollmentID, enrollmentSchedulingRequest);
	}

	private buildNoteString(initialNote: string | null, additionalNote: string | null): string | null {
		if (initialNote && additionalNote) {
			return initialNote + ' ' + additionalNote;
		} else if (initialNote) {
			return initialNote;
		} else if (additionalNote) {
			return additionalNote;
		} else {
			return null;
		}
	}

	private loadEnrollmentStatus(): void {
		this._enrollmentService
			.getEnrollmentStatuses()
			.pipe(take(1))
			.subscribe((statuses) => this._enrollmentStatuses.set(statuses));
	}
}
